import React from 'react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { DashboardPanel, ActionPill, AvatarCircle, getEmployeeInitials, inputStyle } from './DashboardUI';
import { Search, Calendar, Plus, Loader2 } from 'lucide-react';

const EmployeesTab = ({
  employees,
  searchTerm, setSearchTerm,
  newEmployeeForm, setNewEmployeeForm,
  handleCreateEmployee,
  isLoading,
  getFilteredEmployees,
  // Modal openers
  setSelectedEmployee,
  setEmployeeStatusUpdate,
  setShowStatusModal,
  setEditPinForm,
  setEditPinDialog,
  setEditNameForm,
  setEditNameDialog,
  setEmployeeToEdit,
  setShowScheduleEditor,
  setEmployeeBreakForm,
  setBreakSettingsDialog,
  setDeleteDialog,
  setShowAllScheduleEditor,
  // Break form defaults
  customization,
}) => (
  <div className="space-y-10" data-testid="employees-tab">
    {/* Add Employee Panel */}
    <DashboardPanel title="Add Employee">
      <form onSubmit={handleCreateEmployee}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 lg:gap-6">
          <Input
            placeholder="First Name"
            value={newEmployeeForm.first_name}
            onChange={(e) => setNewEmployeeForm({...newEmployeeForm, first_name: e.target.value})}
            className={inputStyle}
            required
          />
          <Input
            placeholder="Last Name"
            value={newEmployeeForm.last_name}
            onChange={(e) => setNewEmployeeForm({...newEmployeeForm, last_name: e.target.value})}
            className={inputStyle}
            required
          />
          <Input
            placeholder="4-Digit PIN"
            value={newEmployeeForm.pin}
            onChange={(e) => setNewEmployeeForm({...newEmployeeForm, pin: e.target.value})}
            className={inputStyle}
            inputMode="numeric"
            maxLength={4}
            required
          />
          <Input
            placeholder="Email (Optional)"
            type="email"
            value={newEmployeeForm.email}
            onChange={(e) => setNewEmployeeForm({...newEmployeeForm, email: e.target.value})}
            className={inputStyle}
          />
        </div>
        <div className="pt-6">
          <Button type="submit" disabled={isLoading} className="w-full bg-emerald-500/20 text-emerald-200 hover:bg-emerald-500/30 rounded-xl border border-emerald-500/30">
            {isLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
            Add Employee
          </Button>
        </div>
      </form>
    </DashboardPanel>

    {/* All Employees Panel */}
    <DashboardPanel title="All Employees">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 pb-6">
        <div className="flex items-center gap-3">
          <Search size={16} className="text-zinc-400" />
          <Input
            placeholder="Search employees"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className={`${inputStyle} w-full md:w-72`}
          />
        </div>
        <Button type="button" onClick={() => setShowAllScheduleEditor(true)} className="bg-zinc-800 text-zinc-200 hover:bg-zinc-700 rounded-xl">
          <Calendar className="w-4 h-4 mr-2" /> Set Schedule for All
        </Button>
      </div>

      {/* Table Header */}
      <div className="hidden md:grid grid-cols-[1.6fr_2fr] gap-4 px-3 pb-3 text-xs text-zinc-400 border-b border-zinc-800">
        <div>Name</div>
        <div className="text-right">Actions</div>
      </div>

      {/* Employee Rows */}
      <div className="space-y-3 pt-3">
        {getFilteredEmployees().length > 0 ? (
          getFilteredEmployees().map((employee) => (
            <div key={employee.id} className="grid grid-cols-1 md:grid-cols-[1.6fr_2fr] items-center gap-4 rounded-2xl border border-zinc-800 bg-zinc-800/40 px-4 py-4">
              <div className="flex items-center gap-3 min-w-0">
                <AvatarCircle initials={getEmployeeInitials(employee.first_name, employee.last_name)} className="h-8 w-8 bg-violet-500/15 text-violet-200" />
                <div className="min-w-0">
                  <div className="text-sm text-white truncate">{employee.first_name} {employee.last_name}</div>
                  {employee.email && <div className="text-xs text-zinc-500 truncate">{employee.email}</div>}
                </div>
              </div>
              <div className="flex flex-wrap gap-2 md:justify-end">
                <ActionPill label="Status" tone="slate" onClick={() => {
                  setSelectedEmployee(employee);
                  setEmployeeStatusUpdate({ employeeId: employee.id, status: employee.current_status || 'active', note: employee.status_note || '', startDate: employee.leave_start_date || '', endDate: employee.leave_end_date || '' });
                  setShowStatusModal(true);
                }} />
                <ActionPill label="View PIN" tone="indigo" onClick={() => {
                  setSelectedEmployee(employee);
                  setEditPinForm({ pin: employee.pin || '' });
                  setEditPinDialog(true);
                }} />
                <ActionPill label="Edit" tone="slate" onClick={() => {
                  setSelectedEmployee(employee);
                  setEditNameForm({ first_name: employee.first_name, last_name: employee.last_name, pin: employee.pin || '', email: employee.email || '' });
                  setEditNameDialog(true);
                }} />
                <ActionPill label="Schedule" tone="indigo" onClick={() => {
                  setEmployeeToEdit(employee);
                  setShowScheduleEditor(true);
                }} />
                <ActionPill label="Breaks" tone="violet" onClick={() => {
                  setSelectedEmployee(employee);
                  setEmployeeBreakForm({
                    custom_break_minutes: employee.custom_break_minutes || customization.standard_break_minutes || 30,
                    custom_work_hours: employee.custom_work_hours || customization.workday_hours || 8.0
                  });
                  setBreakSettingsDialog(true);
                }} />
                <ActionPill label="Delete" tone="rose" onClick={() => {
                  setSelectedEmployee(employee);
                  setDeleteDialog(true);
                }} />
              </div>
            </div>
          ))
        ) : (
          <div className="text-center py-8 text-zinc-400">
            {searchTerm ? 'No employees match your search' : 'No employees yet. Add your first employee above.'}
          </div>
        )}
      </div>
    </DashboardPanel>
  </div>
);

export default EmployeesTab;
